/**
 * This service works as a bridge between the `auth` module and the SSO API.
 * @ngesdoc service
 * @ngname auth
 * @ngmodule auth
 * @ngrequires common
 */
class Auth {
  /**
   * @param {AppAPI} appAPI To make the requests to the SSO API.
   */
  constructor(appAPI) {
    'ngInject';

    /**
     * A local reference for the `appAPI` service.
     * @type {AppAPI}
     */
    this.appAPI = appAPI;
    /**
     * A simple flag to track if the user is being authenticated using crentials or a third party
     * service (like Okta).
     * The reason this flag is on this service is that we can change it to `true` whenever the user
     * calls `login(...)` and if he/she gets to the success screen, the flag value will be
     * maintained (because this an SPA); but if the user goes to a third party service, he/she will
     * be leaving the application, so the flag will be resetted.
     * @type {boolean}
     * @access protected
     * @ignore
     */
    this._usingCredentials = false;
  }
  /**
   * Returns the user information for a given token and a hash the client can use to identify
   * when the session (token) changed.
   * @param {string} token The user authentication token.
   * @return {Promise<Object,Error>}
   */
  createUserSession(token) {
    return this.appAPI.createUserSession(token);
  }
  /**
   * Gets the accounts assigned to a user.
   * @param {string} token              The user authentication token.
   * @param {Array}  [permissionsScope] A list of permissions scopes to filter the accounts.
   * @return {Promise<Object,Error>}
   */
  getAccounts(token, permissionsScope) {
    return this.appAPI.getAccounts(token, permissionsScope)
    .then((response) => {
      const newResponse = {
        user: {
          name: response.name,
          id: response.id,
          email: response.email,
        },
        accounts: response.customer_accounts,
      };

      if (response.scopes) {
        newResponse.scope = response.scopes.shift();
      }

      if (response.application) {
        newResponse.application = response.application;
      }

      return newResponse;
    });
  }
  /**
   * Gets an application information by its name.
   * @param {string} name The application name.
   * @return {Promise<Object,Error>}
   */
  getApplication(name) {
    return this.appAPI.getApplication(name);
  }
  /**
   * Gets the list of avaiable SSO providers for a user and whether the user can login with his/her
   * Olapic account.
   * @param {string} email The user email address.
   * @return {Promise<Object,Error>}
   */
  getProvidersInformation(email) {
    return this.appAPI.getSSOProviders(email)
    .then((response) => {
      // Format the providers list and add their authentication URL through the API.
      const providers = response.providers
      .map((provider) => Object.assign({}, provider, {
        url: this.appAPI.endpoint('auth.loginWithProvider', {
          name: provider.id,
        }),
      }));

      return {
        providers,
        olapicAccountEnabled: response.olapicAccountEnabled,
        olapicHomeEnabled: response.olapicHomeEnabled,
      };
    });
  }
  /**
   * Gets the URL for a referrer used on the reset password process.
   * @param {string} code    The secret code to reset the password. Part of the link sent to the
   *                         user via email.
   * @param {string} refCode The referrer code. Part of the link sent to the user via email.
   * @return {Promise<Object,Error>}
   */
  getReferrerURL(code, refCode) {
    return this.appAPI.getResetPasswordReferrer(code, refCode)
    .then((ref) => ref.url);
  }
  /**
   * Gets a permissions scope information by its name.
   * @param {string} name The scope name.
   * @return {Promise<Object,Error>}
   */
  getScope(name) {
    return this.appAPI.getScope(name);
  }
  /**
   * Gets all the information for a given token.
   * @param {string} token The user authentication token.
   * @return {Promise<Object,Error>}
   */
  getTokenInfo(token) {
    return this.appAPI.getTokenInfo(token);
  }
  /**
   * Checks whether the user was authenticated using basic credentials or not.
   * @return {boolean}
   */
  isTheUserAuthenticatedWithCredentials() {
    return this._usingCredentials;
  }
  /**
   * Authenticates a user using email and password credentials.
   * @param {string} email    The user email.
   * @param {string} password The user password.
   * @return {Promise<Object,Error>}
   */
  login(email, password) {
    return this.appAPI.login(email, password)
    .then((response) => {
      this._usingCredentials = true;
      return Object.assign(
        {
          token: response.tokeninfo.access_token,
        },
        response,
      );
    });
  }
  /**
   * Sends a request to reset the user password.
   * @param {string}  email                The user email address.
   * @param {Object}  [options={}]         Custom options to build the referrer code, which will
   *                                       allow the user to return to the right implementation
   *                                       once the reset password process is finished.
   * @param {?string} options.application  An application name.
   * @param {?string} options.accountScope The name of a permissions scope.
   * @return {Promise<Object,Error>}
   */
  rememberPassword(email, options = {}) {
    return this.appAPI.rememberPassword(email, options);
  }
  /**
   * Resets a user password.
   * @param {string} code     The secret code to reset the password. Part of the link sent to the
   *                          user via email.
   * @param {string} password The new password.
   * @return {Promise<Object,Error>}
   */
  resetPassword(code, password) {
    return this.appAPI.resetPassword(code, password);
  }
  /**
   * Validate whether a user has permissions for an application or not.
   * @param {string} token The user authentication token.
   * @param {string} name  The application name.
   * @return {Promise<Object,Error>}
   */
  validateApplicationPermissions(token, name) {
    return this.appAPI.getApplicationPermissions(token, name)
    .then((response) => !!response.permissions.length);
  }
}

export default Auth;
